// ========================================
// CAMERA CONFIGURATION
// ========================================
// Central configuration file for all camera behavior settings
// Adjust these values to customize the tree visualization experience

export const CAMERA_CONFIG = {
  // ========================================
  // FOCUS & POSITIONING
  // ========================================
  // Controls the vertical alignment of focused nodes
  // These values divide the viewport height to determine where the node appears
  // Examples:
  //   2 = node at center (height/2)
  //   3 = node at 1/3 from top (more centered)
  //   4 = node at 1/4 from top (higher on screen, more room for children below)
  //   5 = node at 1/5 from top (even higher)

  // Vertical alignment for nodes WITH CHILDREN EXPANDED
  // Higher divisor = node positioned higher on screen (more room to see children below)
  // Recommended range: 3-5
  FOCUS_Y_WITH_CHILDREN: 4,

  // Vertical alignment for nodes WITHOUT CHILDREN or with CHILDREN COLLAPSED
  // Lower divisor = node more centered on screen (no need to see children below)
  // Recommended range: 2-3
  FOCUS_Y_WITHOUT_CHILDREN: 2,

  // ========================================
  // ZOOM LEVELS
  // ========================================

  // Maximum zoom level for leaf nodes (no children)
  // Higher = more zoomed in (0.95 = 95% of full size)
  MAX_ZOOM_LEAF: 1.2,

  // Maximum zoom level for nodes with children
  // Lower to show more context when children are present
  MAX_ZOOM_WITH_CHILDREN: 0.8,

  // Minimum zoom level (for very large trees)
  // Prevents zooming out too far
  MIN_ZOOM: 0.20,  // Lowered to allow more zoom out in landscape

  // Re-clicking a card that's already focused zooms in closer instead of
  // doing nothing. Index 0 = the normal first-click zoom (multiplier 1),
  // index 1 = second click, index 2 = third click (the closest, and the cap —
  // further clicks stay at this level). Clicking a different card resets to index 0.
  ZOOM_STEP_MULTIPLIERS: [1, 1.6, 2.2],

  // Initial zoom level when tree first loads
  INITIAL_ZOOM: 0.18,

  // Initial camera position X offset
  INITIAL_CAMERA_X_OFFSET: -(250 + 800) * 0.18,

  // Initial camera position Y offset
  INITIAL_CAMERA_Y_OFFSET: -400 * 0.18,

  // ========================================
  // ANIMATION
  // ========================================

  // Duration of camera movement animation (milliseconds)
  ANIMATION_DURATION: 800,

  // Easing function for smooth camera transitions
  // Options: 'linear', 'ease-in', 'ease-out', 'ease-in-out'
  ANIMATION_EASING: 'ease-in-out',

  // Duration for node and line transitions when expanding/collapsing (milliseconds)
  NODE_TRANSITION_DURATION: 500,

  // Easing for node and line transitions
  NODE_TRANSITION_EASING: 'ease-out',

  // ========================================
  // CAMERA MOVEMENT
  // ========================================

  // Base speed for keyboard camera movement (WASD/Arrow keys)
  // Higher = faster movement
  KEYBOARD_MOVE_SPEED: 5,

  // Mouse wheel zoom sensitivity
  // Higher = more zoom per scroll
  ZOOM_SENSITIVITY: 0.001,

  // Minimum change to trigger zoom (prevents tiny accidental zooms)
  MIN_ZOOM_DELTA: 0.001,

  // ========================================
  // LAYOUT - WORLD DIMENSIONS
  // ========================================

  // Total world width (pixels)
  WORLD_WIDTH: 12000,

  // Total world height (pixels)
  WORLD_HEIGHT: 8000,

  // Vertical spacing between tree levels (pixels)
  LEVEL_HEIGHT: 455,

  // Subtree padding (pixels)
  SUBTREE_PADDING: 250,

  // ========================================
  // LAYOUT - NODE DIMENSIONS & SPACING
  // ========================================

  // Maximum width for nodes to prevent horizontal overlap (pixels)
  MAX_NODE_WIDTH: 300,

  // Minimum horizontal spacing between sibling nodes (pixels)
  MIN_NODE_SPACING: 20,

  // Minimum vertical spacing between nodes (pixels)
  MIN_VERTICAL_SPACING: 80,

  // ========================================
  // VIEWPORT & CULLING
  // ========================================

  // Padding around viewport for rendering nodes (pixels)
  // Nodes outside this area won't be rendered (performance optimization)
  VIEWPORT_PADDING: 500,

  // ========================================
  // MINIMAP
  // ========================================

  // Scale factor for minimap (smaller = more overview)
  MINIMAP_SCALE: 0.08,

  // Minimap viewport indicator stroke width
  MINIMAP_VIEWPORT_STROKE: 3,

  // ========================================
  // SEARCH & FOCUS
  // ========================================

  // Padding around focused nodes when searching (pixels)
  FOCUS_PADDING: 350,

  // Minimum width/height for focused context view (pixels)
  MIN_FOCUS_WIDTH: 700,
  MIN_FOCUS_HEIGHT: 500,
};

// Helper function to get vertical position divisor based on whether node has children
export const getVerticalPositionDivisor = (hasChildren) => {
  return hasChildren
    ? CAMERA_CONFIG.FOCUS_Y_WITH_CHILDREN
    : CAMERA_CONFIG.FOCUS_Y_WITHOUT_CHILDREN;
};

// Helper function to get zoom level based on whether node has children
export const getMaxZoom = (hasChildren) => {
  return hasChildren
    ? CAMERA_CONFIG.MAX_ZOOM_WITH_CHILDREN
    : CAMERA_CONFIG.MAX_ZOOM_LEAF;
};

// Calculate final zoom ensuring it stays within bounds
export const calculateFinalZoom = (targetZoom) => {
  return Math.max(
    Math.min(targetZoom, CAMERA_CONFIG.MAX_ZOOM_LEAF),
    CAMERA_CONFIG.MIN_ZOOM
  );
};

export default CAMERA_CONFIG;
